import { Fragment, useMemo, type ComponentProps, type ReactNode } from 'react'

import { Link, type LinkProps } from '@tanstack/react-router'
import { Home } from 'lucide-react'

import { Heading } from '@/components/ui/heading'

import { cx } from '@/libs/cva'

import { Badge } from '../badge'
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../breadcrumb'

function Root({ className, ...rest }: ComponentProps<'div'>) {
	return <div className={cx('flex flex-col gap-4', className)} {...rest} />
}

function Header({
	title,
	breadcrumbs,
}: {
	title?: string
	breadcrumbs?: ReactNode | BreadcrumbItem[]
}) {
	const _breadcrumbs = useMemo(() => {
		if (Array.isArray(breadcrumbs))
			return <PageBreadcrumbs items={breadcrumbs} />
		return breadcrumbs
	}, [breadcrumbs])

	return (
		<div className={cx('flex flex-col gap-4 py-2', title && 'pb-0')}>
			{_breadcrumbs}
			{title && (
				<Heading level="h1" className="font-bold">
					{title}
				</Heading>
			)}
		</div>
	)
}

function ContentSingleColumn({ className, ...rest }: ComponentProps<'div'>) {
	return <div className={cx('flex flex-col gap-4', className)} {...rest} />
}

type BreadcrumbItem = [to: LinkProps['to'], label: string]

function buildBreadcrumbs(items: BreadcrumbItem[]) {
	const _items = [
		['/', <Home className="h-3 w-3" key="home" />] as const,
		...(items ?? []),
	]
	const length = _items.length
	return {
		items: _items,
		length,
	}
}

function PageBreadcrumbs({ items: _items }: { items: BreadcrumbItem[] }) {
	const { items, length } = useMemo(() => buildBreadcrumbs(_items), [_items])

	return (
		<Breadcrumb>
			<BreadcrumbList className="!gap-1">
				{items.map(([to, label], i) => (
					<Fragment key={i}>
						<BreadcrumbItem className="text-xs">
							{i === length - 1 ? (
								<BreadcrumbPage>
									<Badge size="2xsmall">{label}</Badge>
								</BreadcrumbPage>
							) : (
								<BreadcrumbLink asChild>
									<Link to={to}>{label}</Link>
								</BreadcrumbLink>
							)}
						</BreadcrumbItem>
						<BreadcrumbSeparator className="last:hidden" />
					</Fragment>
				))}
			</BreadcrumbList>
		</Breadcrumb>
	)
}

export const PageLayout = Object.assign(Root, {
	Root,
	Header,
	Breadcrumbs: PageBreadcrumbs,
	ContentSingleColumn,
})
